// src/Views/PrivacyPolicy/PrivacyPolicy.js
// STANDALONE Privacy Policy page for App Store submission
// Route: /privacy-policy

import React from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
    const navigate = useNavigate();

    return (
        <div style={{
            minHeight: '100vh',
            background: '#0f172a',
            color: '#e2e8f0',
            paddingBottom: '3rem',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
        }}>
            {/* Header */}
            <div style={{
                background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
                padding: '2rem 1.5rem',
                textAlign: 'center',
                borderBottom: '1px solid rgba(71, 85, 105, 0.3)'
            }}>
                <button 
                    onClick={() => navigate(-1)}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#60a5fa',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '0.9rem',
                        marginBottom: '1.5rem',
                        padding: '8px 12px',
                        borderRadius: '6px'
                    }}
                >
                    <Icon icon="solar:arrow-left-linear" /> Back
                </button>
                <h1 style={{
                    fontSize: '2rem',
                    fontWeight: '700',
                    color: '#fff',
                    marginBottom: '0.5rem'
                }}>
                    Privacy Policy
                </h1>
                <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                    Last Updated: February 16, 2026
                </p>
            </div>

            {/* Content */}
            <div style={{
                maxWidth: '800px',
                margin: '0 auto',
                padding: '2rem 1.5rem'
            }}>
                
                <Section title="1. Introduction">
                    <p>
                        AiPredict ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our mobile application. By using the Service, you consent to the data practices described in this policy.
                    </p>
                </Section>

                <Section title="2. Information We Collect">
                    <p>We collect information in several ways to provide and improve our Service:</p>
                    <ul style={listStyle}>
                        <ListItem title="Personal Identification Information">
                            When you register, we collect your name, email address, and authentication credentials (via Firebase).
                        </ListItem>
                        <ListItem title="Payment Information">
                            For premium subscriptions, transactions are processed by third-party gateways (Apple In-App Purchase, Stripe, Kowri). For Crypto AI Bot subscriptions, we record the USDT transaction hash and wallet address for payment verification. We do not store credit card details.
                        </ListItem>
                        <ListItem title="Exchange API Credentials">
                            If you enroll in the automated trading service, we collect your exchange API key and API secret. These credentials are encrypted immediately upon submission and stored in encrypted form only. They are decrypted solely at the time of trade execution and are never logged, cached, or exposed in plain text.
                        </ListItem>
                        <ListItem title="Trading Data">
                            We record trade activity executed by the bot on your account, including entry/exit prices, position sizes, profit/loss figures, and trade timestamps. This data is used to provide your dashboard and improve our systems.
                        </ListItem>
                        <ListItem title="Usage Data">
                            We automatically collect data on how you interact with the Service, including the AI queries you submit, pages visited, time spent, and clicks.
                        </ListItem>
                        <ListItem title="Device & Technical Data">
                            We collect your IP address, browser type, operating system, and device identifiers for security and rate-limiting purposes.
                        </ListItem>
                    </ul>
                </Section>

                <Section title="3. How We Use Your Information">
                    <p>We use the collected data for the following purposes:</p>
                    <ul style={listStyle}>
                        <ListItem title="Service Provision">
                            To operate the Platform, authenticate your identity, execute trades on your behalf, and grant access to paid features.
                        </ListItem>
                        <ListItem title="Trade Execution">
                            Your encrypted API credentials are used exclusively to place, modify, and close trades on your connected exchange account as part of the Crypto AI Bot service.
                        </ListItem>
                        <ListItem title="AI Processing">
                            Your questions and data inputs are sent to our AI providers to generate predictions. These queries are anonymized where possible.
                        </ListItem>
                        <ListItem title="Communication">
                            To send you administrative information, trade notifications, payment confirmations, security alerts, and support responses.
                        </ListItem>
                        <ListItem title="Security & Fraud Prevention">
                            To monitor for suspicious activity, prevent unauthorized access, and enforce our Terms of Service.
                        </ListItem>
                        <ListItem title="Improvement">
                            To analyze system performance, optimize our trading algorithms, and improve the user experience.
                        </ListItem>
                    </ul>
                </Section>

                <Section title="4. Sharing of Information">
                    <p>We do not sell, trade, or rent your personal identification information to others. We may share information with:</p>
                    <ul style={listStyle}>
                        <ListItem title="Service Providers">
                            Third-party vendors who perform services on our behalf, such as hosting (Google Cloud/Firebase), payment processing (Apple, Stripe), and email delivery.
                        </ListItem>
                        <ListItem title="Exchanges">
                            Your API credentials are transmitted securely to your connected exchange (e.g., Bybit) solely for trade execution. We do not share your credentials with any other party.
                        </ListItem>
                        <ListItem title="Legal Requirements">
                            We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or a government agency).
                        </ListItem>
                        <ListItem title="Business Transfers">
                            If AiPredict is involved in a merger, acquisition, or asset sale, your personal data may be transferred. Encrypted API keys would either be transferred under the same security standards or deleted.
                        </ListItem>
                    </ul>
                </Section>

                <Section title="5. API Key Security">
                    <p>
                        If you enroll in the automated trading service, we take the security of your exchange API credentials extremely seriously:
                    </p>
                    <ul style={listStyle}>
                        <ListItem>API keys are encrypted before storage and are never stored in plain text.</ListItem>
                        <ListItem>Decryption occurs only at the moment of trade execution, in server memory, and keys are not written to logs or temporary files.</ListItem>
                        <ListItem>Access to encryption systems is restricted and monitored.</ListItem>
                        <ListItem>Upon account deletion or subscription cancellation, your API keys are permanently deleted from our systems within 30 days.</ListItem>
                        <ListItem>We strongly recommend that users configure API keys with trading-only permissions and no withdrawal access. We are not responsible for losses resulting from misconfigured API permissions.</ListItem>
                    </ul>
                </Section>

                <Section title="6. Cookies and Tracking Technologies">
                    <p>
                        We use cookies, local storage, and similar tracking technologies to track the activity on our Service and hold certain information.
                    </p>
                    <div style={{ marginTop: '1rem' }}>
                        <p style={{ marginBottom: '0.5rem' }}><strong>Essential Cookies:</strong> Required for authentication (keeping you logged in).</p>
                        <p><strong>Analytics Cookies:</strong> Help us understand how you use the site.</p>
                    </div>
                    <p style={{ marginTop: '1rem' }}>
                        You can instruct your browser to refuse all cookies, but if you do not accept cookies, you may not be able to use some portions of our Service.
                    </p>
                </Section>

                <Section title="7. Data Retention">
                    <p>We retain your personal data only for as long as necessary. Specific retention periods:</p>
                    <ul style={listStyle}>
                        <ListItem title="Account Data">Retained while your account is active, deleted within 30 days of account deletion.</ListItem>
                        <ListItem title="API Keys">Retained while your bot subscription is active, deleted within 30 days of cancellation or account deletion.</ListItem>
                        <ListItem title="Trade History">Retained for up to 12 months after your subscription ends for performance reporting and dispute resolution.</ListItem>
                        <ListItem title="Payment Records">Retained for up to 5 years as required for financial record-keeping and tax compliance.</ListItem>
                        <ListItem title="Anonymized Analytics">May be retained indefinitely for system improvement purposes.</ListItem>
                    </ul>
                </Section>

                <Section title="8. Data Security">
                    <p>
                        The security of your data is important to us. We use industry-standard encryption for data in transit and at rest. Your account is secured by Firebase Authentication. Sensitive data such as API keys receive additional layers of encryption and are never stored in plain text. However, no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your data, we cannot guarantee its absolute security.
                    </p>
                </Section>

                <Section title="9. International Data Transfers">
                    <p>
                        Your information, including Personal Data, may be transferred to—and maintained on—computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ from those of your jurisdiction. By using the Service, you consent to this transfer.
                    </p>
                </Section>

                <Section title="10. Your Data Rights">
                    <p>Depending on your location, you may have the following rights:</p>
                    <ul style={listStyle}>
                        <ListItem title="Access">The right to request copies of your personal data.</ListItem>
                        <ListItem title="Rectification">The right to request that we correct any information you believe is inaccurate.</ListItem>
                        <ListItem title="Erasure">The right to request that we erase your personal data ("Right to be Forgotten"), subject to legal retention requirements.</ListItem>
                        <ListItem title="Data Portability">The right to request that we transfer data we have collected to another organization, or directly to you.</ListItem>
                        <ListItem title="API Key Deletion">You may request immediate deletion of your stored API keys at any time by contacting support or deleting your bot enrollment through the app.</ListItem>
                    </ul>
                    <p style={{ marginTop: '1rem' }}>To exercise these rights, please contact us at <a href="mailto:support@aipredict.app" style={{ color: '#60a5fa', textDecoration: 'none' }}>support@aipredict.app</a>.</p>
                </Section>

                <Section title="11. Account Deletion">
                    <p>
                        You can delete your account at any time through the app's Profile settings. When you delete your account:
                    </p>
                    <ul style={listStyle}>
                        <ListItem>Your personal profile data will be permanently removed from our systems.</ListItem>
                        <ListItem>Your authentication credentials will be deleted from Firebase.</ListItem>
                        <ListItem>Any stored exchange API keys will be permanently deleted.</ListItem>
                        <ListItem>The bot will stop trading on your behalf immediately, but any open positions will remain on your exchange and become your responsibility to manage.</ListItem>
                        <ListItem>Any active subscriptions will not renew.</ListItem>
                        <ListItem>Some anonymized usage data may be retained for analytics purposes.</ListItem>
                    </ul>
                </Section>

                <Section title="12. Children's Privacy">
                    <p>
                        Our Service does not address anyone under the age of 18 ("Children"). We do not knowingly collect personally identifiable information from anyone under the age of 18. If you are a parent or guardian and you are aware that your child has provided us with Personal Data, please contact us. If we become aware that we have collected Personal Data from children without verification of parental consent, we take steps to remove that information from our servers.
                    </p>
                </Section>

                <Section title="13. Third-Party Services">
                    <p>Our app uses the following third-party services that may collect information:</p>
                    <ul style={listStyle}>
                        <ListItem title="Firebase (Google)">Authentication, database, and analytics</ListItem>
                        <ListItem title="RevenueCat">Subscription management for iOS</ListItem>
                        <ListItem title="Apple App Store">In-app purchases and payment processing</ListItem>
                        <ListItem title="AI Agents">AI processing for predictions and trading signals</ListItem>
                        <ListItem title="Bybit (and future exchanges)">Trade execution via API for automated trading service subscribers. Your API credentials are transmitted securely to the exchange solely for trade execution.</ListItem>
                    </ul>
                    <p>Please refer to their respective privacy policies for more information on how they handle your data.</p>
                </Section>

                <Section title="14. Changes to This Privacy Policy">
                    <p>
                        We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
                    </p>
                </Section>

                <Section title="15. Contact Us">
                    <p>If you have any questions about this Privacy Policy, please contact us:</p>
                    <ul style={listStyle}>
                        <li><strong>Email:</strong> <a href="mailto:support@aipredict.app" style={{ color: '#60a5fa', textDecoration: 'none' }}>support@aipredict.app</a></li>
                        <li><strong>Website:</strong> <a href="https://aipredict.app" target="_blank" rel="noreferrer" style={{ color: '#60a5fa', textDecoration: 'none' }}>https://aipredict.app</a></li>
                    </ul>
                </Section>

            </div>

            {/* Footer */}
            <div style={{
                textAlign: 'center',
                padding: '2rem 1.5rem',
                borderTop: '1px solid rgba(71, 85, 105, 0.3)',
                marginTop: '2rem',
                background: '#0f172a'
            }}>
                <p style={{ color: '#64748b', fontSize: '0.85rem' }}>
                    © {new Date().getFullYear()} AiPredict. All rights reserved.
                </p>
                <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', gap: '1.5rem' }}>
                    <button 
                        onClick={() => navigate('/legal?tab=terms')}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#64748b',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            textDecoration: 'underline'
                        }}
                    >
                        Terms of Service
                    </button>
                    <button 
                        onClick={() => navigate('/legal?tab=risk')}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#64748b',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            textDecoration: 'underline'
                        }}
                    >
                        Risk Disclosure
                    </button>
                </div>
            </div>
        </div>
    );
};

// Reusable Section component
const Section = ({ title, children }) => (
    <section style={{
        marginBottom: '2rem',
        paddingBottom: '1.5rem',
        borderBottom: '1px solid rgba(71, 85, 105, 0.2)'
    }}>
        <h2 style={{
            fontSize: '1.25rem',
            fontWeight: '600',
            color: '#60a5fa',
            marginBottom: '1rem',
            letterSpacing: '-0.5px'
        }}>
            {title}
        </h2>
        <div style={{
            color: '#cbd5e1',
            lineHeight: '1.7',
            fontSize: '0.95rem'
        }}>
            {children}
        </div>
    </section>
);

// Helper for List Items
const ListItem = ({ title, children }) => (
    <li style={{ marginBottom: '0.8rem', paddingLeft: '1rem', borderLeft: '2px solid #334155' }}>
        {title && <strong style={{ color: '#e2e8f0', display: 'block', marginBottom: '0.2rem' }}>{title}:</strong>}
        <span style={{ color: '#94a3b8' }}>{children}</span>
    </li>
);

// List style
const listStyle = {
    listStyle: 'none',
    padding: 0,
    margin: '1rem 0'
};

export default PrivacyPolicy;